import { useMemo, useState } from 'react';
import { Badge, Button, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUserSlash } from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';

import { useOperators } from '../../features/operators/hooks/useOperators';
import { useAuthContext } from '../../providers/AuthProvider.js';
// `http`, not a raw fetch: :5100 refuses an untokened call with a 401 whose
// only trace is a line in the server log.
import { http } from '../../features/jobs/api/http';

const SHIFTS = ['A', 'B', 'C'];

/**
 * WHO IS ON THIS MACHINE — the panel's operator box, on the web.
 *
 * The machine panel has had this since the operators module shipped: the name
 * is picked from the active operators of a shift, stored per machine, and the
 * production the line records is attributed to it. Asked for here on
 * 2026-11-05 — *"Am nevoie de acelasi sistem de logare de operator, sa apara
 * undeva pe pagina cu posiblitate de schimbare, exact ca in
 * machine_hmi_interface"* — because the queue is the screen a planner is
 * standing at when they notice the wrong name on a run.
 *
 * Same endpoint as the panel (`/machines/:id/operator`), so a name set here and
 * a name set at the line are the same fact, and the same list
 * (`/api/v1/operators?active=true`) grouped by shift.
 *
 * The CURRENT shift is not invented. There is no shift-rotation helper in the
 * code to reuse, and guessing an anchor would put the wrong tab in front of the
 * operator — so the tab opens on the shift of whoever is already selected, and
 * on A when nobody is, which is exactly what the panel falls back to.
 *
 * @param machineId  lowercase machine id
 * @param operator   the name the queue payload reports, or null
 * @param shift      the selected operator's shift, when the payload knows it
 */
const OperatorBox = ({ machineId, operator, shift = null, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const qc = useQueryClient();

  // WHO MAY CHANGE IT. `domainAuth` classifies `POST /machines/:id/operator` as
  // a PRODUCTION write, so a salesperson gets a 403 with a Romanian message —
  // and that middleware's own note says a run of those is "a UI offering a
  // button it should have hidden". The name is still shown to everybody: who is
  // on a machine is a fact worth reading from any page.
  const [authState] = useAuthContext();
  const role = authState?.user?.role;
  const canChange = role === 'admin' || role === 'production';

  const { data, isLoading } = useOperators({ active: true });

  const byShift = useMemo(() => {
    const rows = Array.isArray(data?.data) ? data.data : [];
    const out = { A: [], B: [], C: [] };
    for (const o of rows) {
      if (o?.active === false) continue;
      const s = String(o?.shift || '').toUpperCase();
      if (out[s]) out[s].push(o);
    }
    for (const s of SHIFTS) out[s].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return out;
  }, [data]);

  const current = tab || String(shift || '').toUpperCase() || 'A';

  const choose = async (name, pickedShift = null) => {
    setBusy(name || 'CLEAR');
    setErr('');
    try {
      // The shift travels, so the box can open on the right tab next time
      // instead of a rotation this code would have to guess.
      await http.post(`/api/redis/machines/${machineId}/operator`, {
        name: name || '',
        ...(pickedShift ? { shift: pickedShift } : {})
      });
      // The queue payload carries the operator, so the page re-reads it from
      // the one place that owns it rather than holding a second copy.
      await qc.invalidateQueries({ queryKey: ['machine', 'queue', machineId] });
      onChanged?.(name || null);
      setOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Could not set the operator');
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <div
        role={canChange ? 'button' : undefined}
        tabIndex={canChange ? 0 : undefined}
        onClick={canChange ? () => setOpen(true) : undefined}
        onKeyDown={
          canChange
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') setOpen(true);
              }
            : undefined
        }
        className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-2 border border-translucent"
        style={{
          cursor: canChange ? 'pointer' : 'default',
          background: 'var(--phoenix-body-bg)'
        }}
        title={
          canChange
            ? 'Who is on this machine — click to change'
            : 'Who is on this machine'
        }
      >
        <FontAwesomeIcon
          icon={operator ? faUser : faUserSlash}
          className={operator ? 'text-primary' : 'text-700'}
        />
        <span className="d-flex flex-column lh-sm">
          <span className="fs-10 text-700">Operator</span>
          <span className={`fs-9 fw-semibold ${operator ? '' : 'text-warning'}`}>
            {operator || 'Not set'}
          </span>
        </span>
        {shift && (
          <Badge bg="secondary" variant="phoenix" className="fs-10">
            {String(shift).toUpperCase()}
          </Badge>
        )}
      </div>

      <Modal show={open} onHide={() => !busy && setOpen(false)} centered>
        <Modal.Header closeButton={!busy}>
          <Modal.Title className="fs-7">
            Operator on {String(machineId || '').toUpperCase()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3">
            {SHIFTS.map(s => (
              <Button
                key={s}
                size="sm"
                variant={current === s ? 'primary' : 'outline-secondary'}
                onClick={() => setTab(s)}
              >
                Shift {s}
                <Badge bg="secondary" variant="phoenix" className="ms-2 fs-10">
                  {byShift[s].length}
                </Badge>
              </Button>
            ))}
          </div>

          {isLoading ? (
            <div className="text-700 fs-9">
              <Spinner animation="border" size="sm" className="me-2" />
              Reading the operator list…
            </div>
          ) : !byShift[current].length ? (
            <div className="text-700 fs-9">No active operator on shift {current}.</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {byShift[current].map(o => {
                const mine = String(o.name) === String(operator);
                return (
                  <Button
                    key={o._id || o.name}
                    variant={mine ? 'success' : 'outline-primary'}
                    className="text-start d-flex align-items-center justify-content-between"
                    disabled={!!busy}
                    onClick={() => choose(o.name, current)}
                  >
                    <span>{o.name}</span>
                    {busy === o.name ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      mine && <Badge bg="success">on the machine</Badge>
                    )}
                  </Button>
                );
              })}
            </div>
          )}

          {err && <div className="text-danger fs-9 mt-3">{err}</div>}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          {/* The panel has this too: a machine with nobody on it is a real
              state, and leaving the last name standing attributes somebody
              else's production to them. */}
          <Button
            variant="outline-danger"
            size="sm"
            disabled={!!busy || !operator}
            onClick={() => choose(null)}
          >
            {busy === 'CLEAR' ? 'Clearing…' : 'Clear operator'}
          </Button>
          <Button variant="outline-secondary" size="sm" disabled={!!busy} onClick={() => setOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OperatorBox;
